import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderBar from "../HeaderBar/HeaderBar";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import {
  importPounceBulkLeads,
  previewPounceBulkLeads,
} from "../../Store/apiStore";
import styles from "./PounceBulkUploadReviewPage.module.css";
const normalizePhone = (value) => String(value ?? "").replace(/\D/g, "");
const normalizeEmail = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const inspectRows = (rows = []) => {
  const firstPhoneRow = new Map();
  const firstEmailRow = new Map();

  return rows.map((row, index) => {
    const name = String(row.name ?? "").trim();
    const phone = String(row.phone ?? "").trim();
    const email = String(row.email ?? "").trim();
    const compactPhone = phone.replace(/[\s().-]/g, "");
    const phoneKey = normalizePhone(phone);
    const emailKey = normalizeEmail(email);
    const rowNumber = row.rowNumber ?? index + 2;
    const issues = [];

    if (!name) issues.push("Missing full name");
    if (!phone) {
      issues.push("Missing phone number");
    } else if (!/^\+?\d{8,13}$/.test(compactPhone)) {
      issues.push("Invalid phone format");
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      issues.push("Invalid email format");
    }

    if (issues.length) {
      return {
        ...row,
        rowNumber,
        name,
        phone,
        email,
        issues,
        category: "invalid",
        duplicateMatches: [],
      };
    }

    const duplicateMatches = [];
    if (phoneKey && firstPhoneRow.has(phoneKey)) {
      duplicateMatches.push({
        by: "phone",
        scope: "file",
        rowNumber: firstPhoneRow.get(phoneKey),
      });
    }
    if (emailKey && firstEmailRow.has(emailKey)) {
      duplicateMatches.push({
        by: "email",
        scope: "file",
        rowNumber: firstEmailRow.get(emailKey),
      });
    }
    if (phoneKey && !firstPhoneRow.has(phoneKey)) {
      firstPhoneRow.set(phoneKey, rowNumber);
    }
    if (emailKey && !firstEmailRow.has(emailKey)) {
      firstEmailRow.set(emailKey, rowNumber);
    }

    return {
      ...row,
      rowNumber,
      name,
      phone,
      email,
      issues: [],
      category: duplicateMatches.length ? "duplicate" : "valid",
      duplicateMatches,
    };
  });
};

const getValidatedRows = (rows, validatedRows) => {
  if (
    Array.isArray(validatedRows) &&
    validatedRows.length === rows.length &&
    validatedRows.every((row) => row && typeof row === "object")
  ) {
    return validatedRows;
  }
  return inspectRows(rows);
};

const getSafeReturnTo = (value, fallback = "/add-lead") =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//")
    ? value
    : fallback;

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "Size unavailable";
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const csvCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;

const getDuplicateMatchGroups = (row, allRows) => {
  const groups = new Map();

  (row.duplicateMatches || []).forEach((match) => {
    const scope = match.scope === "account" ? "account" : "file";
    const key =
      scope === "account"
        ? `account:${match.existingLead?.id || match.existingLead?.phone || "match"}`
        : `file:${match.rowNumber}`;

    if (!groups.has(key)) {
      groups.set(key, {
        scope,
        rowNumber: match.rowNumber,
        existingLead: match.existingLead || null,
        matchTypes: [],
      });
    }
    if (!groups.get(key).matchTypes.includes(match.by)) {
      groups.get(key).matchTypes.push(match.by);
    }
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    matchedRow:
      group.scope === "account"
        ? group.existingLead
        : allRows.find(
            (candidate) =>
              String(candidate.rowNumber) === String(group.rowNumber),
          ),
  }));
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const PounceBulkUploadReviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state ?? {};
  const rows = Array.isArray(routeState.rows) ? routeState.rows : [];
  const fileName = routeState.fileName || "Uploaded file";
  const fileSize = Number(routeState.fileSize || 0);
  const fileUrl = routeState.fileUrl || null;
  const returnTo = getSafeReturnTo(routeState.returnTo);
  const uploadedFile =
    routeState.uploadedFile ||
    routeState.file ||
    (fileName ? { name: fileName, size: fileSize } : null);

  const localRows = useMemo(
    () => getValidatedRows(rows, routeState.validatedRows),
    [rows, routeState.validatedRows],
  );
  const [serverRows, setServerRows] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [detailView, setDetailView] = useState(null);
  const [visibleDetailCount, setVisibleDetailCount] = useState(50);
  const [hasConsent, setHasConsent] = useState(routeState.hasConsent === true);
  const [actionMessage, setActionMessage] = useState(
    routeState.actionMessage || "",
  );
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const inspectedRows = serverRows || localRows;
  const previewReady = Array.isArray(serverRows);
  const invalidRows = inspectedRows.filter((row) => row.category === "invalid");
  const duplicateRows = inspectedRows.filter(
    (row) => row.category === "duplicate",
  );
  const validRows = inspectedRows.filter((row) => row.category === "valid");

  useEffect(() => {
    if (!rows.length) {
      navigate("/bulk-upload", { replace: true, state: { returnTo } });
    }
  }, [navigate, returnTo, rows.length]);

  useEffect(() => {
    if (!rows.length) return undefined;

    let active = true;
    setIsPreviewing(true);
    setPreviewError("");
    setServerRows(null);

    const safeRows = rows.map(
      ({ rowNumber, name, phone, email, source, company }) => ({
        rowNumber,
        name,
        phone,
        email,
        source,
        company,
      }),
    );

    previewPounceBulkLeads(safeRows)
      .then((result) => {
        const validatedRows = Array.isArray(result?.rows) ? result.rows : null;
        if (!validatedRows || validatedRows.length !== rows.length) {
          throw new Error(
            "The server returned an incomplete validation result.",
          );
        }
        if (active) setServerRows(validatedRows);
      })
      .catch((error) => {
        if (active) {
          setPreviewError(
            getApiErrorMessage(
              error,
              "Could not check existing account leads.",
            ),
          );
        }
      })
      .finally(() => {
        if (active) setIsPreviewing(false);
      });

    return () => {
      active = false;
    };
  }, [rows, previewAttempt]);

  useEffect(() => {
    setVisibleDetailCount(50);
  }, [detailView]);

  const goBackToUpload = () =>
    navigate("/bulk-upload", {
      state: {
        returnTo,
        rows,
        fileName,
        fileSize,
        fileUrl,
        uploadedFile,
        file: uploadedFile,
      },
    });
  const chooseDifferentFile = () =>
    navigate("/bulk-upload", { state: { returnTo } });
  const goBackToReview = () => {
    setDetailView(null);
    setActionMessage("");
  };

  const downloadErrorReport = () => {
    const heading = [
      "Row",
      "Full Name",
      "Phone Number",
      "Email Address",
      "Issue",
    ];
    const reportRows = invalidRows.map((row) => [
      row.rowNumber,
      row.name,
      row.phone,
      row.email,
      (row.issues || []).join("; "),
    ]);
    const content = [heading, ...reportRows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF", content], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "outbound-caller-invalid-leads.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleImport = async () => {
    if (!hasConsent) {
      setActionMessage("Please confirm consent before importing.");
      return;
    }
    if (!previewReady || isPreviewing) {
      setActionMessage(
        "Wait for the server validation to finish before importing.",
      );
      return;
    }
    if (!validRows.length) return;

    const candidates = rows.map(
      ({ rowNumber, name, phone, email, source, company }) => ({
        rowNumber,
        name,
        phone,
        email,
        source,
        company,
      }),
    );

    setIsImporting(true);
    setActionMessage("");
    setImportResult(null);

    try {
      const result = await importPounceBulkLeads(candidates, true);
      if (!Array.isArray(result?.rows) || !result?.summary) {
        throw new Error("The server did not return row-level import results.");
      }
      const summary = result.summary;
      const importedCount = Number(summary.imported) || 0;

      if (importedCount > 0) {
        navigate("/bulk-upload/success", {
          replace: true,
          state: {
            importResult: result,
            fileName,
            fileSize,
            returnTo,
          },
        });
        return;
      }

      setImportResult(result);
      setActionMessage(
        `Import finished: ${summary.imported || 0} imported, ${summary.duplicate || 0} duplicates, ${summary.invalid || 0} invalid, ${summary.failed || 0} failed${summary.unverified ? `, ${summary.unverified} unverified` : ""}.`,
      );
    } catch (error) {
      setActionMessage(
        getApiErrorMessage(error, "Import failed. Please try again."),
      );
    } finally {
      setIsImporting(false);
    }
  };

  if (!rows.length) return null;

  const isDetailView = Boolean(detailView);
  const importedRows = importResult?.rows || [];
  const detailTitle =
    detailView === "invalid"
      ? "Invalid Leads"
      : detailView === "import"
        ? "Import Results"
        : "Duplicate Leads";
  const detailRows =
    detailView === "invalid"
      ? invalidRows
      : detailView === "import"
        ? importedRows
        : duplicateRows;
  const detailLabel =
    detailView === "invalid"
      ? "invalid lead"
      : detailView === "import"
        ? "row result"
        : "duplicate lead";

  return (
    <div className={styles.page}>
      <div className={styles.stickyTop}>
        <HeaderBar
          title=""
          subtitle={<OutboundCallerBrand />}
          onBack={isDetailView ? goBackToReview : goBackToUpload}
        />
      </div>

      {isDetailView ? (
        <main className={`${styles.main} ${styles.detailMain}`}>
          <h1 className={styles.title}>{detailTitle}</h1>
          <p className={styles.subtitle}>
            {detailView === "invalid"
              ? "These rows need correction before they can be imported."
              : detailView === "import"
                ? "These are the server-confirmed outcomes for each uploaded row."
                : "These rows match another row in this file or an existing account lead and will be skipped."}
          </p>
          <p className={styles.detailCount}>
            {detailRows.length} {detailLabel}
            {detailRows.length === 1 ? "" : "s"}
          </p>

          {detailRows.length > 0 ? (
            <div className={styles.detailList}>
              {detailView === "invalid"
                ? invalidRows.slice(0, visibleDetailCount).map((row) => (
                    <article
                      className={styles.detailCard}
                      key={`invalid-${row.rowNumber}`}
                    >
                      <span
                        className={`${styles.detailStatus} ${styles.invalidStatus}`}
                        aria-hidden="true"
                      >
                        !
                      </span>
                      <div className={styles.detailCopy}>
                        <small>Row {row.rowNumber}</small>
                        <strong>{row.name || "—"}</strong>
                        <span>{row.email || "No email"}</span>
                        <span>{row.phone || "No phone"}</span>
                      </div>
                      <span className={styles.reasonBadge}>
                        {(row.issues || []).join(" · ") || "Needs correction"}
                      </span>
                    </article>
                  ))
                : detailView === "import"
                  ? importedRows
                      .slice(0, visibleDetailCount)
                      .map((row, index) => (
                        <article
                          className={styles.detailCard}
                          key={`result-${row.rowNumber}-${index}`}
                        >
                          <span
                            className={`${styles.detailStatus} ${row.status === "imported" ? styles.importedStatus : row.status === "duplicate" ? styles.duplicateStatus : styles.invalidStatus}`}
                            aria-hidden="true"
                          >
                            {row.status === "imported"
                              ? "✓"
                              : row.status === "duplicate"
                                ? "↔"
                                : "!"}
                          </span>
                          <div className={styles.detailCopy}>
                            <small>
                              Row {row.rowNumber} ·{" "}
                              {row.status || "not imported"}
                            </small>
                            <strong>{row.name || "—"}</strong>
                            <span>{row.email || "No email"}</span>
                            <span>{row.phone || "No phone"}</span>
                          </div>
                          <span
                            className={`${styles.reasonBadge} ${row.status === "imported" ? styles.importedBadge : ""}`}
                          >
                            {row.result ||
                              (row.issues || []).join(" · ") ||
                              "Result unavailable"}
                          </span>
                        </article>
                      ))
                  : duplicateRows.slice(0, visibleDetailCount).map((row) => {
                      const matchGroups = getDuplicateMatchGroups(
                        row,
                        inspectedRows,
                      );
                      const primaryMatch = matchGroups[0];
                      const extraMatches = matchGroups.slice(1);
                      const matchedRow = primaryMatch?.matchedRow;

                      return (
                        <article
                          className={`${styles.detailCard} ${styles.duplicateDetailCard}`}
                          key={`duplicate-${row.rowNumber}`}
                        >
                          <span
                            className={`${styles.detailStatus} ${styles.duplicateStatus}`}
                            aria-hidden="true"
                          >
                            ↔
                          </span>
                          <div className={styles.duplicateContent}>
                            <div className={styles.duplicateHeader}>
                              <small>
                                Duplicate lead · Row {row.rowNumber}
                              </small>
                              <span className={styles.duplicateBadge}>
                                Duplicate by{" "}
                                {primaryMatch?.matchTypes.join(" + ") ||
                                  "matching details"}
                              </span>
                            </div>
                            <div className={styles.duplicateComparison}>
                              <section className={styles.compareColumn}>
                                <small>Incoming lead</small>
                                <strong>{row.name || "—"}</strong>
                                <span>{row.email || "No email"}</span>
                                <span>{row.phone || "No phone"}</span>
                              </section>
                              <section className={styles.compareColumn}>
                                <small>
                                  {primaryMatch?.scope === "account"
                                    ? "Matching existing account lead"
                                    : `Matching row in this file · Row ${primaryMatch?.rowNumber ?? "—"}`}
                                </small>
                                <strong>{matchedRow?.name || "—"}</strong>
                                <span>{matchedRow?.email || "No email"}</span>
                                <span>{matchedRow?.phone || "No phone"}</span>
                              </section>
                            </div>
                            {extraMatches.map((match, index) => (
                              <p
                                className={styles.extraMatch}
                                key={`${match.scope}-${match.rowNumber || index}-${match.matchTypes.join("-")}`}
                              >
                                {match.scope === "account"
                                  ? "Also matches an existing account lead"
                                  : `Also matches row ${match.rowNumber}`}{" "}
                                by {match.matchTypes.join(" + ")}
                              </p>
                            ))}
                          </div>
                        </article>
                      );
                    })}
            </div>
          ) : (
            <p className={styles.emptyDetail}>
              There are no {detailLabel}s in this upload.
            </p>
          )}

          {detailRows.length > visibleDetailCount && (
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={() =>
                setVisibleDetailCount((count) =>
                  Math.min(count + 50, detailRows.length),
                )
              }
            >
              Show 50 more ({detailRows.length - visibleDetailCount} remaining)
            </button>
          )}

          <div className={styles.detailActions}>
            {detailView === "invalid" && invalidRows.length > 0 && (
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={downloadErrorReport}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3v12m0 0 4-4m-4 4-4-4M5 17v3h14v-3"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Download Error Report
              </button>
            )}
            <button
              className={styles.primaryButton}
              type="button"
              onClick={goBackToReview}
            >
              Back to Review
            </button>
          </div>
        </main>
      ) : (
        <main className={`${styles.main} ${styles.summaryMain}`}>
          <h1 className={styles.title}>Validation Review</h1>
          <p className={styles.subtitle}>
            We’ve checked your file and your account’s existing leads. Review
            the results and confirm before importing.
          </p>

          <section className={styles.fileCard} aria-label="Uploaded file">
            <span className={styles.fileBadge} aria-hidden="true">
              {String(fileName).toLowerCase().endsWith(".xlsx")
                ? "XLSX"
                : "CSV"}
            </span>
            <span className={styles.fileCopy}>
              {fileUrl ? (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={`Open ${fileName}`}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  <strong>{fileName}</strong>
                </a>
              ) : (
                <strong title={fileName}>{fileName}</strong>
              )}

              <small>
                {rows.length} rows · {formatFileSize(fileSize)}
              </small>
            </span>
            <button
              className={styles.removeFile}
              type="button"
              onClick={chooseDifferentFile}
              aria-label="Choose a different file"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 7h16M9 7V4h6v3m3 0-.8 13H6.8L6 7m4 4v5m4-5v5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </section>

          {isPreviewing && (
            <p className={styles.actionMessage} role="status">
              Checking rows against existing account leads…
            </p>
          )}
          {previewError && (
            <div className={styles.previewError} role="alert">
              <span>
                {previewError} Import is disabled until the account check
                succeeds.
              </span>
              <button
                type="button"
                onClick={() => setPreviewAttempt((attempt) => attempt + 1)}
              >
                Retry check
              </button>
            </div>
          )}

          <section
            className={styles.summaryCard}
            aria-labelledby="summary-title"
          >
            <h2 id="summary-title">Validation Summary</h2>
            <div className={`${styles.summaryRow} ${styles.validRow}`}>
              <span
                className={`${styles.statusIcon} ${styles.validIcon}`}
                aria-hidden="true"
              >
                ✓
              </span>
              <strong className={styles.count}>{validRows.length}</strong>
              <span className={styles.rowLabel}>Valid Leads</span>
            </div>
            <div className={`${styles.summaryRow} ${styles.invalidRow}`}>
              <span
                className={`${styles.statusIcon} ${styles.invalidIcon}`}
                aria-hidden="true"
              >
                ×
              </span>
              <strong className={styles.count}>{invalidRows.length}</strong>
              <span className={styles.rowLabel}>Invalid Leads</span>
              <button
                className={styles.viewButton}
                type="button"
                onClick={() => setDetailView("invalid")}
                disabled={!invalidRows.length}
              >
                View <span aria-hidden="true">›</span>
              </button>
            </div>
            <div className={`${styles.summaryRow} ${styles.duplicateRow}`}>
              <span
                className={`${styles.statusIcon} ${styles.duplicateIcon}`}
                aria-hidden="true"
              >
                ⧉
              </span>
              <strong className={styles.count}>{duplicateRows.length}</strong>
              <span className={styles.rowLabel}>Duplicates</span>
              <button
                className={styles.viewButton}
                type="button"
                onClick={() => setDetailView("duplicates")}
                disabled={!duplicateRows.length}
              >
                View <span aria-hidden="true">›</span>
              </button>
            </div>
          </section>

          <p className={styles.duplicateNote}>
            Duplicates are checked against this file and existing Pounce leads
            for your account. Notes are not imported.
          </p>

          <div className={styles.consent} style={{ cursor: "default" }}>
            <input
              type="checkbox"
              checked={hasConsent}
              disabled={isImporting || Boolean(importResult)}
              onChange={(event) => {
                setHasConsent(event.target.checked);
                setActionMessage("");
              }}
              style={{
                cursor:
                  isImporting || Boolean(importResult)
                    ? "not-allowed"
                    : "pointer",
              }}
            />
            <span style={{ cursor: "default", userSelect: "none" }}>
              I confirm these leads have given permission to be contacted.
            </span>
          </div>

          <button
            className={styles.primaryButton}
            type="button"
            disabled={
              !hasConsent ||
              !previewReady ||
              isPreviewing ||
              isImporting ||
              validRows.length === 0 ||
              Boolean(importResult)
            }
            onClick={handleImport}
          >
            {isImporting
              ? "Importing leads…"
              : importResult
                ? `Import finished: ${importResult.summary.imported || 0} imported`
                : `Import ${validRows.length} Valid Leads`}
          </button>
          {actionMessage && (
            <p className={styles.actionMessage} role="status">
              {actionMessage}
            </p>
          )}
          {importResult && (
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={() => setDetailView("import")}
            >
              View Import Results
            </button>
          )}
        </main>
      )}
    </div>
  );
};

export default PounceBulkUploadReviewPage;
